/**
 * StaffCalendar Component
 * Expandable banner showing staff vacations, call offs, and time off
 * Only owners/co-owners can add/edit entries - staff can view only
 */
import { useState, useEffect } from "react";
import { CalendarDays, Plus, X, Trash2, ChevronDown, ChevronUp, Printer, Download, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import axios from "axios";
import { jsPDF } from "jspdf";
import { useAuth } from "@/contexts/AuthContext";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { hasOwnerPermissions } from "@/utils/permissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Entry type options with emojis
const ENTRY_TYPES = [
  { value: "vacation", label: "Vacation", emoji: "🏖️" },
  { value: "sick", label: "Sick / Call Off", emoji: "🤒" },
  { value: "personal", label: "Personal Day", emoji: "📅" },
  { value: "appointment", label: "Appointment", emoji: "🏥" },
  { value: "training", label: "Training", emoji: "📋" },
  { value: "travel", label: "Travel", emoji: "✈️" },
  { value: "other", label: "Other", emoji: "📌" },
];

const StaffCalendar = ({ currentUser }) => {
  const { token } = useAuth();
  const { hasAccess: hasStaffCalendarAccess } = useFeatureAccess('staff_calendar');
  const [entries, setEntries] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    staffId: "",
    staffName: "",
    staffEmail: "",
    type: "vacation",
    startDate: "",
    endDate: "",
    note: "",
  });

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  
  // Auth headers for API calls
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  
  // Check if current user is owner or co-owner (includes test accounts and staff with viewAsTier)
  const isOwnerOrCoOwner = hasOwnerPermissions(currentUser);

  // Fetch entries and staff list on mount
  useEffect(() => {
    if (token) {
      fetchEntries();
      if (isOwnerOrCoOwner) {
        fetchStaffList();
      }
    }
  }, [isOwnerOrCoOwner, token]);

  const fetchStaffList = async () => {
    try {
      const response = await axios.get(`${API}/staff-records`, { headers: authHeaders });
      // Map staff records to the format expected by the dropdown
      const staffRecords = response.data.records || [];
      setStaffList(staffRecords.map(record => ({
        id: record.id,
        name: record.name,
        email: record.email
      })));
    } catch (error) {
      console.error("Error fetching staff list:", error);
    }
  };

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/staff-calendar?year=${currentYear}`, { headers: authHeaders });
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error("Error fetching staff calendar:", error);
    }
  };

  const getEntryEmoji = (type) => {
    const entry = ENTRY_TYPES.find(t => t.value === type);
    return entry?.emoji || "📌";
  };

  const formatDateRange = (startDate, endDate) => {
    const start = parseISO(startDate);
    const startFormatted = format(start, "MMM d");
    
    if (!endDate || startDate === endDate) {
      return startFormatted;
    }
    
    const end = parseISO(endDate);
    // Check if same month
    if (start.getMonth() === end.getMonth()) {
      return `${format(start, "MMM d")}-${format(end, "d")}`;
    }
    return `${startFormatted} - ${format(end, "MMM d")}`;
  };

  const handleAdd = async () => {
    if (!formData.staffId) {
      toast.error("Please select a staff member");
      return;
    }
    if (!formData.startDate) {
      toast.error("Please select a start date");
      return;
    }

    try {
      const entryData = {
        staffName: formData.staffName,
        staffEmail: formData.staffEmail,
        type: formData.type,
        startDate: formData.startDate,
        endDate: formData.endDate || formData.startDate,
        note: formData.note || "",
      };

      console.log("Adding staff calendar entry:", entryData);
      
      const response = await axios.post(`${API}/staff-calendar`, entryData, { headers: authHeaders });
      console.log("Response:", response.data);
      
      toast.success(`Time off added for ${formData.staffName}!`);
      fetchEntries();
      resetForm();
    } catch (error) {
      console.error("Error adding entry:", error.response?.data || error.message);
      toast.error(error.response?.data?.detail || "Failed to add time off");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Remove this entry?")) return;
    
    try {
      await axios.delete(`${API}/staff-calendar/${id}`, { headers: authHeaders });
      toast.success("Entry removed");
      fetchEntries();
    } catch (error) {
      console.error("Error deleting entry:", error);
      toast.error("Failed to remove entry");
    }
  };

  const resetForm = () => {
    setFormData({ staffId: "", staffName: "", staffEmail: "", type: "vacation", startDate: "", endDate: "", note: "" });
    setShowAddModal(false);
  };

  // Print summary report
  const handlePrintSummary = (e) => {
    e.stopPropagation();
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Popup blocked! Please allow popups to print.");
      return;
    }

    const today = format(new Date(), "MMMM d, yyyy");
    
    // Group entries by type
    const groupedEntries = entries.reduce((acc, entry) => {
      const type = ENTRY_TYPES.find(t => t.value === entry.type);
      const label = type?.label || entry.type;
      if (!acc[label]) acc[label] = [];
      acc[label].push(entry);
      return acc;
    }, {});

    const formatEntryDate = (start, end) => {
      const startDate = parseISO(start);
      if (!end || start === end) {
        return format(startDate, "MMM d, yyyy");
      }
      const endDate = parseISO(end);
      return `${format(startDate, "MMM d")} - ${format(endDate, "MMM d, yyyy")}`;
    };

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Staff Time Off Summary</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          h1 { color: #1e40af; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; font-size: 24px; }
          h2 { color: #1e40af; margin-top: 25px; font-size: 18px; }
          .date { color: #6b7280; font-size: 14px; margin-bottom: 20px; }
          .section { margin-bottom: 20px; }
          .section-title { background: #eff6ff; padding: 8px 12px; border-left: 4px solid #3b82f6; margin-bottom: 10px; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th { background: #f3f4f6; text-align: left; padding: 10px; border-bottom: 2px solid #d1d5db; }
          td { padding: 10px; border-bottom: 1px solid #e5e7eb; }
          .emoji { font-size: 18px; }
          .no-entries { color: #6b7280; font-style: italic; text-align: center; padding: 20px; }
          .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280; text-align: center; }
          @media print {
            body { padding: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>📅 Staff Time Off Summary</h1>
        <p class="date">Generated: ${today}</p>
        
        ${entries.length === 0 ? `
          <p class="no-entries">No upcoming time off scheduled.</p>
        ` : `
          <table>
            <thead>
              <tr>
                <th style="width: 30%">Staff Member</th>
                <th style="width: 20%">Type</th>
                <th style="width: 30%">Dates</th>
                <th style="width: 20%">Notes</th>
              </tr>
            </thead>
            <tbody>
              ${entries.map(entry => {
                const type = ENTRY_TYPES.find(t => t.value === entry.type);
                return `
                  <tr>
                    <td><strong>${entry.staffName}</strong></td>
                    <td><span class="emoji">${type?.emoji || '📌'}</span> ${type?.label || entry.type}</td>
                    <td>${formatEntryDate(entry.startDate, entry.endDate)}</td>
                    <td>${entry.note || '-'}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          
          <h2>Summary by Type</h2>
          ${Object.entries(groupedEntries).map(([type, typeEntries]) => `
            <div class="section">
              <div class="section-title">${type} (${typeEntries.length})</div>
              <ul>
                ${typeEntries.map(e => `<li>${e.staffName} - ${formatEntryDate(e.startDate, e.endDate)}</li>`).join('')}
              </ul>
            </div>
          `).join('')}
        `}
        
        <div class="footer">
          Staff Calendar Report • Total Entries: ${entries.length}
        </div>
        
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Download PDF report
  const handleDownloadPDF = (e) => {
    e.stopPropagation();
    
    try {
      const pdf = new jsPDF();
      const today = format(new Date(), "MMMM d, yyyy");
      const pageWidth = pdf.internal.pageSize.getWidth();
      let y = 20;

      // Title
      pdf.setFontSize(20);
      pdf.setTextColor(30, 64, 175);
      pdf.text("Staff Time Off Summary", pageWidth / 2, y, { align: "center" });
      y += 10;

      // Date
      pdf.setFontSize(10);
      pdf.setTextColor(107, 114, 128);
      pdf.text(`Generated: ${today}`, pageWidth / 2, y, { align: "center" });
      y += 15;

      if (entries.length === 0) {
        pdf.setFontSize(12);
        pdf.setTextColor(107, 114, 128);
        pdf.text("No upcoming time off scheduled.", pageWidth / 2, y, { align: "center" });
      } else {
        // Table header
        pdf.setFillColor(243, 244, 246);
        pdf.rect(15, y - 5, pageWidth - 30, 10, "F");
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        pdf.setFont("helvetica", "bold");
        pdf.text("Staff Member", 20, y);
        pdf.text("Type", 70, y);
        pdf.text("Dates", 110, y);
        pdf.text("Notes", 160, y);
        y += 10;

        // Table rows
        pdf.setFont("helvetica", "normal");
        entries.forEach((entry) => {
          if (y > 270) {
            pdf.addPage();
            y = 20;
          }

          const type = ENTRY_TYPES.find(t => t.value === entry.type);
          const startDate = parseISO(entry.startDate);
          let dateStr = format(startDate, "MMM d, yyyy");
          if (entry.endDate && entry.endDate !== entry.startDate) {
            const endDate = parseISO(entry.endDate);
            dateStr = `${format(startDate, "MMM d")} - ${format(endDate, "MMM d")}`;
          }

          pdf.setTextColor(0, 0, 0);
          pdf.text(entry.staffName?.substring(0, 20) || "Unknown", 20, y);
          pdf.text(type?.label || entry.type, 70, y);
          pdf.text(dateStr, 110, y);
          pdf.text((entry.note || "-").substring(0, 20), 160, y);
          
          // Draw line
          pdf.setDrawColor(229, 231, 235);
          pdf.line(15, y + 3, pageWidth - 15, y + 3);
          y += 10;
        });

        // Summary
        y += 10;
        pdf.setFontSize(12);
        pdf.setTextColor(30, 64, 175);
        pdf.setFont("helvetica", "bold");
        pdf.text("Summary by Type", 20, y);
        y += 8;

        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(0, 0, 0);

        const groupedEntries = entries.reduce((acc, entry) => {
          const type = ENTRY_TYPES.find(t => t.value === entry.type);
          const label = type?.label || entry.type;
          if (!acc[label]) acc[label] = 0;
          acc[label]++;
          return acc;
        }, {});

        Object.entries(groupedEntries).forEach(([type, count]) => {
          pdf.text(`• ${type}: ${count}`, 25, y);
          y += 6;
        });
      }

      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(107, 114, 128);
      pdf.text(`Total Entries: ${entries.length}`, pageWidth / 2, 285, { align: "center" });

      // Download
      pdf.save(`staff-time-off-${format(new Date(), "yyyy-MM-dd")}.pdf`);
      toast.success("PDF downloaded!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const hasEntries = entries.length > 0;

  // Don't render if user doesn't have access
  if (!hasStaffCalendarAccess) {
    return null;
  }

  return (
    <>
      {/* Staff Calendar Banner */}
      <div 
        className="w-full rounded-xl overflow-hidden transition-all duration-300"
        style={{
          background: 'linear-gradient(135deg, rgba(239,246,255,0.95) 0%, rgba(224,231,255,0.9) 50%, rgba(239,246,255,0.95) 100%)',
          boxShadow: '0 2px 8px rgba(59,130,246,0.1), inset 0 1px 0 rgba(255,255,255,0.8)',
          border: '1px solid rgba(59,130,246,0.2)'
        }}
      >
        {/* Header Row - Always Visible */}
        <div 
          className="flex items-center gap-3 px-4 py-2 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {/* Icon & Title */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <CalendarDays className="w-5 h-5 text-blue-600" />
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-semibold text-blue-800 text-sm cursor-help flex items-center gap-1">
                    Staff Calendar
                    <Info className="w-3.5 h-3.5 text-blue-400" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-blue-800 text-white border-blue-700 max-w-[220px]">
                  <p className="text-xs">Contact your Director to add time off or schedule changes</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {hasEntries && (
              <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                {entries.length}
              </span>
            )}
          </div>

          {/* Collapsed Preview - Show entries inline when collapsed */}
          {!isExpanded && hasEntries && (
            <div className="flex-1 overflow-hidden">
              <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
                <span className="text-gray-400">|</span>
                {entries.map((entry, idx) => (
                  <span key={entry.id} className="flex items-center gap-1 whitespace-nowrap">
                    {idx > 0 && <span className="text-gray-400 mx-1">•</span>}
                    <span>{getEntryEmoji(entry.type)}</span>
                    <span className="font-medium">{entry.staffName?.split(' ')[0]}</span>
                    <span className="text-gray-500">- {ENTRY_TYPES.find(t => t.value === entry.type)?.label}</span>
                    <span className="text-blue-600 font-medium">({formatDateRange(entry.startDate, entry.endDate)})</span>
                    {entry.note && <span className="text-green-600">- {entry.note}</span>}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Expand/Collapse & Add Button */}
          <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
            {/* Download PDF Button - Owners only */}
            {hasEntries && isOwnerOrCoOwner && (
              <button
                onClick={handleDownloadPDF}
                className="p-1.5 rounded-full bg-green-100 hover:bg-green-200 transition-colors"
                title="Download PDF Report"
              >
                <Download className="w-4 h-4 text-green-600" />
              </button>
            )}
            {/* Print Summary Button - Owners only */}
            {hasEntries && isOwnerOrCoOwner && (
              <button
                onClick={handlePrintSummary}
                className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                title="Print Summary Report"
              >
                <Printer className="w-4 h-4 text-gray-600" />
              </button>
            )}
            {isExpanded && isOwnerOrCoOwner && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowAddModal(true); }}
                className="p-1.5 rounded-full bg-blue-500 hover:bg-blue-600 transition-colors"
                title="Add Time Off"
              >
                <Plus className="w-4 h-4 text-white" />
              </button>
            )}
            {isExpanded && !isOwnerOrCoOwner && (
              <div className="relative group">
                <div 
                  className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full cursor-help"
                >
                  <Info className="w-3 h-3" />
                  <span>View Only</span>
                </div>
                {/* Subtle tooltip */}
                <div className="absolute right-0 top-full mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                  <div className="bg-gray-800 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap">
                    To request time off, speak with your Director
                  </div>
                </div>
              </div>
            )}
            {hasEntries && (
              isExpanded ? (
                <ChevronUp className="w-5 h-5 text-blue-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-blue-500" />
              )
            )}
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && hasEntries && (
          <div className="px-4 pb-3 border-t border-blue-100">
            {/* Staff instruction - only for non-owners */}
            {!isOwnerOrCoOwner && (
              <div className="text-xs text-gray-500 pt-2 pb-1 flex items-center gap-1">
                <Info className="w-3 h-3" />
                <span>To request time off, please speak with your Director</span>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 pt-2">
              {entries.map((entry, idx) => (
                <div 
                  key={entry.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-blue-200 text-sm group"
                >
                  <span className="text-base">{getEntryEmoji(entry.type)}</span>
                  <span className="font-medium text-gray-800">{entry.staffName}</span>
                  <span className="text-gray-500">-</span>
                  <span className="text-blue-700">{ENTRY_TYPES.find(t => t.value === entry.type)?.label}</span>
                  <span className="text-gray-400">({formatDateRange(entry.startDate, entry.endDate)})</span>
                  {/* Display note if exists */}
                  {entry.note && (
                    <span className="text-green-600 font-medium ml-1">- {entry.note}</span>
                  )}
                  
                  {/* Delete button - only for owners/co-owners */}
                  {isOwnerOrCoOwner && (
                    <button
                      onClick={() => handleDelete(entry.id)}
                      className="p-0.5 rounded hover:bg-red-100 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  )}
                  
                  {idx < entries.length - 1 && (
                    <span className="text-gray-300 ml-1 hidden sm:inline">•</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Expanded but empty - show add prompt for owners only */}
        {isExpanded && !hasEntries && (
          <div className="px-4 pb-3 border-t border-blue-100">
            <div className="flex items-center justify-center gap-3 pt-3 text-gray-500">
              <span className="text-sm">No time off scheduled</span>
              {isOwnerOrCoOwner ? (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAddModal(true); }}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Entry
                </button>
              ) : (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Contact Director to request time off
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Entry Modal - Only for Owners/Co-Owners */}
      <Dialog open={showAddModal} onOpenChange={resetForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-blue-600" />
              Add Staff Time Off
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Staff Member Selection */}
            <div>
              <Label htmlFor="staffSelect">Staff Member</Label>
              <select
                id="staffSelect"
                value={formData.staffId}
                onChange={(e) => {
                  const selectedStaff = staffList.find(s => s.id === e.target.value || s._id === e.target.value);
                  if (selectedStaff) {
                    setFormData({ 
                      ...formData, 
                      staffId: selectedStaff.id || selectedStaff._id,
                      staffName: selectedStaff.name,
                      staffEmail: selectedStaff.email
                    });
                  }
                }}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a staff member...</option>
                {staffList.map((staff) => (
                  <option key={staff.id || staff._id} value={staff.id || staff._id}>
                    {staff.name} ({staff.email})
                  </option>
                ))}
              </select>
            </div>

            {/* Type Selection */}
            <div>
              <Label>Type</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {ENTRY_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, type: type.value })}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                      formData.type === type.value 
                        ? 'bg-blue-100 border-blue-400 text-blue-800' 
                        : 'bg-white border-gray-200 hover:border-blue-200'
                    }`}
                  >
                    <span className="text-lg">{type.emoji}</span>
                    <span>{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Date Selection */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="endDate">End Date (optional)</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Note */}
            <div>
              <Label htmlFor="note">Note (optional)</Label>
              <Input
                id="note"
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder="e.g., Doctor appointment at 2pm"
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={resetForm}>
              Cancel
            </Button>
            <Button 
              onClick={handleAdd}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Add Time Off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StaffCalendar;
